import { useCallback, useEffect, useState } from 'react'
import { Link, NavLink, Navigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { ApiError } from '@orisini/ui/lib/api'
import { cn } from '@orisini/ui/lib/utils'
import { Avatar } from '../components/Avatar'
import { StatusBadge } from '../components/StatusBadge'
import { OverviewTab } from '../components/OverviewTab'
import { CreateTab } from '../components/CreateTab'
import { TopicsTab } from '../components/TopicsTab'
import { FormatsTab } from '../components/FormatsTab'
import { WorkflowTab } from '../components/WorkflowTab'
import { BrandTab } from '../../brand/components/BrandTab'
import { PublishingTab } from '../components/PublishingTab'
import { ConnectionTab } from '../components/ConnectionTab'
import { accountsApi, type Workspace } from '../lib/accounts'

/** Tab project. Tanpa `ready` = fasenya belum sampai (docs/rencana-perombakan.md). */
const TABS = [
  { key: 'overview', label: 'Overview', ready: true },
  { key: 'create', label: 'Create', ready: true },
  { key: 'topics', label: 'Topics', ready: true },
  { key: 'brand', label: 'Brand & Styles', ready: true },
  { key: 'formats', label: 'Content Formats', ready: true },
  { key: 'workflow', label: 'Workflow', ready: true },
  { key: 'publishing', label: 'Publishing', ready: true },
  { key: 'connection', label: 'Account', ready: true },
] as const

type TabKey = (typeof TABS)[number]['key']

/**
 * /projects/:id/:tab — satu project (baris `workspaces` di orisini-studio).
 *
 * Tab ada di URL supaya refresh mendarat di tab yang sama (pola yang sama
 * dengan /admin/:tab).
 */
export default function ProjectPage() {
  const { id = '', tab = 'overview' } = useParams()
  const [workspace, setWorkspace] = useState<Workspace | null>(null)
  const [missing, setMissing] = useState(false)

  const muat = useCallback(async () => {
    try {
      setWorkspace(await accountsApi.workspace(id))
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setMissing(true)
      else toast.error(err instanceof Error ? err.message : "Couldn't load this project")
    }
  }, [id])

  useEffect(() => {
    setWorkspace(null)
    setMissing(false)
    void muat()
  }, [muat])

  const aktif = TABS.find((t) => t.key === tab && t.ready)
  if (!aktif) return <Navigate to={`/projects/${id}`} replace />
  if (missing) {
    return (
      <div className="space-y-2">
        <h1 className="text-xl font-semibold">Project not found</h1>
        <Link to="/projects" className="text-sm text-muted-foreground hover:text-foreground">Back to projects</Link>
      </div>
    )
  }
  if (!workspace) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Loading…
      </div>
    )
  }

  const handle = workspace.account?.username ? `@${workspace.account.username}` : null

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <Link to="/projects" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="size-4" /> Projects
        </Link>
        <header className="flex flex-wrap items-center gap-4">
          <Avatar url={workspace.account?.avatarUrl ?? null} name={workspace.name} size="lg" />
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-2xl font-semibold tracking-tight">{workspace.name}</h1>
            <p className="truncate text-sm text-muted-foreground">
              {workspace.productCode ? [handle, workspace.platform].filter(Boolean).join(' · ') : 'No account connected · download only'}
            </p>
          </div>
          <StatusBadge status={workspace.status} />
        </header>
      </div>

      {/* overflow-y-hidden: tanpanya -mb-px pada tab memunculkan scrollbar vertikal kecil. */}
      <nav className="-mx-1 flex gap-1 overflow-x-auto overflow-y-hidden border-b border-border px-1" aria-label="Project sections">
        {TABS.map((t) =>
          t.ready ? (
            <NavLink
              key={t.key}
              to={t.key === 'overview' ? `/projects/${id}` : `/projects/${id}/${t.key}`}
              end
              className={({ isActive }) =>
                cn(
                  '-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm',
                  isActive ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground',
                )
              }
            >
              {t.label}
            </NavLink>
          ) : (
            <span
              key={t.key}
              className="-mb-px flex cursor-default items-center gap-1.5 whitespace-nowrap border-b-2 border-transparent px-3 py-2 text-sm text-muted-foreground/60"
              aria-disabled="true"
            >
              {t.label}
              <span className="rounded bg-muted px-1 py-0.5 text-[10px] font-medium uppercase tracking-wide">Soon</span>
            </span>
          ),
        )}
      </nav>

      {(aktif.key as TabKey) === 'overview' && <OverviewTab workspace={workspace} />}
      {aktif.key === 'create' && <CreateTab workspace={workspace} />}
      {aktif.key === 'topics' && <TopicsTab workspace={workspace} />}
      {aktif.key === 'brand' && <BrandTab workspace={workspace} />}
      {aktif.key === 'formats' && <FormatsTab workspace={workspace} />}
      {aktif.key === 'workflow' && <WorkflowTab workspace={workspace} />}
      {aktif.key === 'publishing' && <PublishingTab workspace={workspace} onChanged={muat} />}
      {aktif.key === 'connection' && <ConnectionTab workspace={workspace} onChanged={muat} />}
    </div>
  )
}
